import * as React from "react";
import { toast } from "sonner";
import {
  type FieldErrors,
  type FieldValues,
  type UseFormReturn,
  FormProvider,
  useFormState,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { focusFirstErrorField, getChangedFields } from "@/common/utils/form";
import { SideClose } from "@/components/icons";
import { ROW_CLICKABLE_ATTR } from "@/components/ui/data-table";
import { EditModeHeader, ViewModeHeader } from "./ModalHeader";
import { ScrollFadeArea } from "./ScrollFadeArea";
import { useConfirmDialogs } from "@/hooks/use-confirm-dialog";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

export interface FormDrawerProps<TFormValues extends FieldValues> {
  /** 대상 UUID */
  uuid: string;
  /** 열림 상태 */
  open: boolean;
  /** 열림 상태 변경 */
  setOpen: (open: boolean) => void;
  /** react-hook-form 인스턴스 */
  form: UseFormReturn<TFormValues>;
  /** 폼 기본값 생성 함수 (API 데이터 → 폼 데이터 매핑) */
  getDefaultValues: () => TFormValues;
  /** 데이터 로딩 중 */
  isLoading: boolean;
  /** 수정 mutation의 mutate 함수 */
  updateMutate: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: { uuid: string; params: any },
    options: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSuccess: (res: any) => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error?: any) => void;
    },
  ) => void;
  /** 삭제 mutation의 mutate 함수 */
  deleteMutate: (
    uuid: string,
    options: {
      onSuccess: () => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error?: any) => void;
    },
  ) => void;
  /** 조회 모드 헤더 제목 */
  viewTitle: string;
  /** 조회 모드 헤더 부제목 */
  viewSubtitle?: string | number;
  /** 조회 모드 제목 옆에 표시할 추가 요소 (예: 상태 Badge) */
  viewTitleExtra?: React.ReactNode;
  /** 조회 모드 제목 아래(헤더 안)에 표시할 요소 (예: 탭 바) */
  viewBelowTitle?: React.ReactNode;
  /** 조회 모드 본문 아래에 고정되는 푸터 (수정 모드에서는 그리지 않는다) */
  viewFooter?: React.ReactNode;
  /** 수정 모드 헤더 제목 */
  editTitle: string;
  /** 등록일시 */
  createdAt?: string | null;
  /** 최근수정일시 */
  updatedAt?: string | null;
  /** 삭제 확인 다이얼로그 제목 */
  deleteDialogTitle: string;
  /** 삭제 확인 다이얼로그 설명 */
  deleteDialogDescription: string;
  /** 저장 확인 다이얼로그 제목 */
  saveDialogTitle: string;
  /** 삭제 성공 콜백 */
  onDeleteSuccess: () => void;
  /** 삭제 실패 콜백 (error 전달 — 409 등 분기용) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDeleteError: (error?: any) => void;
  /** 수정 성공 콜백 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateSuccess: (res: any) => void;
  /** 수정 실패 콜백 (error 전달 — 409 등 분기용) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateError: (error?: any) => void;
  /** 변경된 필드만 전송 (기본: true). false이면 전체 폼 데이터를 전송 */
  useChangedFieldsOnly?: boolean;
  /** 삭제 버튼 클릭 시 커스텀 핸들러. defaultHandler를 호출하면 기본 삭제 플로우 진행 */
  onDeleteClick?: (defaultHandler: () => void) => void;
  /**
   * 폼 검증 실패 시 커스텀 에러 핸들러 (제공 시 기본 토스트 대신 호출).
   * 필수값 누락과 형식 오류를 가르려면 값이 필요해 현재 값도 함께 넘긴다
   */
  onFormError?: (errors: FieldErrors<TFormValues>, values: TFormValues) => void;
  /** 트리거 셀 콘텐츠. 제공 시 텍스트+아이콘 전체가 클릭 영역이 됨 */
  triggerContent?: React.ReactNode;
  /** 트리거 버튼을 그리지 않는다 — 목록의 행 클릭(`onRowClick`)이 여는 화면용 */
  hideTrigger?: boolean;
  /** 쓰기 권한 (기본 true). false 이면 수정 버튼을 그리지 않는다 */
  canWrite?: boolean;
  /** 수정·삭제 버튼을 아예 숨긴다 (그 소속에게 존재하지 않는 기능일 때) */
  hideActions?: boolean;
  /**
   * 권한은 있으나 상황상 수정할 수 없는 사유 (로딩 중, 플랫폼 계정 등).
   * 주면 버튼이 사유 툴팁과 함께 비활성으로 남는다 — 권한 부족과 구분한다
   */
  writeDisabledReason?: string;
  /** 삭제 권한 (기본 canWrite). 수정은 허용하되 삭제만 막는 경우에 지정 */
  canDelete?: boolean;
  /** 삭제만의 비활성 사유 (기본 writeDisabledReason) */
  deleteDisabledReason?: string;
  /** 폼 컨텐츠 (render prop) */
  children: (props: {
    isEditMode: boolean;
    isLoading: boolean;
  }) => React.ReactNode;
}

/**
 * 수정/상세 드로어 래퍼 컴포넌트
 * Drawer 셸, 수정/조회 모드 전환, 확인 다이얼로그, 저장/삭제 플로우 등 공통 보일러플레이트를 처리
 *
 * @example
 * ```tsx
 * export default function StationUpdateDrawer({ uuid }: { uuid: string }) {
 *   const [open, setOpen] = React.useState(false);
 *   const { data, isLoading } = useStationInfo(uuid, open);
 *   const form = useForm({ resolver: zodResolver(schema), defaultValues: DEFAULT });
 *   const { mutate: update } = useUpdateStation();
 *   const { mutate: remove } = useDeleteStation();
 *
 *   const getDefaultValues = useCallback(() => {
 *     if (!data) return DEFAULT;
 *     return { name: data.name, ... };
 *   }, [data]);
 *
 *   return (
 *     <FormDrawer
 *       uuid={uuid} open={open} setOpen={setOpen}
 *       form={form} getDefaultValues={getDefaultValues} isLoading={isLoading}
 *       updateMutate={update} deleteMutate={remove}
 *       viewTitle={data?.name || "충전소명"} editTitle="충전소 수정"
 *       deleteDialogTitle="충전소를 삭제할까요?" deleteDialogDescription="삭제된 충전소는 복구할 수 없습니다."
 *       saveDialogTitle="충전소 정보를 저장할까요?"
 *       onDeleteSuccess={() => toast.success("삭제했어요.")}
 *       onDeleteError={() => toast.error("삭제 실패")}
 *       onUpdateSuccess={(res) => toast.success(`‘${res.name}’ 수정했어요.`)}
 *       onUpdateError={() => toast.error("수정 실패")}
 *     >
 *       {({ isEditMode, isLoading }) => (
 *         <Section title="기본 정보" isLoading={isLoading}>
 *           <FormTextInput disabled={!isEditMode} ... />
 *         </Section>
 *       )}
 *     </FormDrawer>
 *   );
 * }
 * ```
 */
export function FormDrawer<TFormValues extends FieldValues>({
  uuid,
  open,
  setOpen,
  form,
  getDefaultValues,
  isLoading,
  updateMutate,
  deleteMutate,
  viewTitle,
  viewSubtitle,
  viewTitleExtra,
  viewBelowTitle,
  viewFooter,
  editTitle,
  createdAt,
  updatedAt,
  deleteDialogTitle,
  deleteDialogDescription,
  saveDialogTitle,
  onDeleteSuccess,
  onDeleteError,
  onUpdateSuccess,
  onUpdateError,
  useChangedFieldsOnly = true,
  onDeleteClick,
  onFormError: onFormErrorProp,
  triggerContent,
  hideTrigger = false,
  canWrite = true,
  hideActions = false,
  writeDisabledReason,
  canDelete = canWrite,
  deleteDisabledReason = writeDisabledReason,
  children,
}: FormDrawerProps<TFormValues>) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const [isEditMode, setIsEditMode] = React.useState(false);
  // 확인 다이얼로그가 닫힌 뒤에도 저장·삭제 버튼은 응답이 올 때까지 살아 있다 —
  // RHF 의 isSubmitting 은 다이얼로그를 여는 순간 이미 끝나 있어 이 플래그가 없으면
  // 빠르게 다시 눌러 같은 요청이 두 번 나간다
  const [isMutating, setIsMutating] = React.useState(false);

  const { handleSubmit, reset, control, setFocus } = form;
  const { isDirty, isSubmitting } = useFormState({ control });

  const [formErrors, setFormErrors] =
    React.useState<FieldErrors<TFormValues> | null>(null);

  const onFormError: Parameters<typeof handleSubmit>[1] = (errors) => {
    if (onFormErrorProp) {
      onFormErrorProp(errors, form.getValues());
    } else {
      toast.error("필수 항목을 모두 입력해 주세요.");
    }
    setFormErrors(errors);
  };

  React.useEffect(() => {
    if (formErrors) {
      focusFirstErrorField(formErrors, setFocus, {
        scrollContainer: scrollRef.current,
      });
    }
  }, [formErrors, setFocus]);

  // drawer가 열릴 때 폼을 서버 값으로 되돌린다 (RHF 는 외부 상태라 effect 가 맞다)
  React.useEffect(() => {
    if (open) reset(getDefaultValues());
  }, [open, reset, getDefaultValues]);

  // 열릴 때는 조회 모드로 시작한다. effect 안에서 setState 하지 않도록
  // 이전 open 값과 비교하는 렌더 중 보정 패턴을 쓴다
  const [wasOpen, setWasOpen] = React.useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setIsEditMode(false);
  }

  const dialogs = useConfirmDialogs({
    delete: {
      title: deleteDialogTitle,
      description: deleteDialogDescription,
      confirmText: "삭제",
      cancelText: "취소",
      variant: "danger",
    },
    exit: {
      title: `편집 중인 내용이 있어요.\n저장하지 않고 나갈까요?`,
      description: "저장하지 않은 내용은 삭제돼요.",
      confirmText: "나가기",
      cancelText: "취소",
      variant: "danger",
    },
    save: {
      title: saveDialogTitle,
      confirmText: "저장",
      cancelText: "취소",
      variant: "primary",
    },
  });

  const handleEditMode = () => {
    if (!canWrite) return; // 읽기전용 방어
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isDirty) {
      dialogs.open("exit", () => {
        setIsEditMode(false);
        reset(getDefaultValues());
      });
    } else {
      setIsEditMode(false);
      reset(getDefaultValues());
    }
  };

  const defaultDeleteHandler = () => {
    dialogs.open("delete", handleConfirmDelete);
  };

  const handleDeleteClick = () => {
    if (!canDelete) return; // 읽기전용 방어 (handleEditMode와 동일한 이유)
    if (onDeleteClick) {
      onDeleteClick(defaultDeleteHandler);
    } else {
      defaultDeleteHandler();
    }
  };

  const handleConfirmDelete = async () => {
    // 중복 제출을 실제로 막는 지점 — 버튼 disabled 는 눈에 보이는 피드백일 뿐이다
    if (isMutating) return;
    setIsMutating(true);
    deleteMutate(uuid, {
      onSuccess: () => {
        setIsMutating(false);
        onDeleteSuccess();
        setOpen(false);
      },
      onError: (error) => {
        setIsMutating(false);
        onDeleteError(error);
      },
    });
  };

  const getChangedData = (): Partial<TFormValues> =>
    getChangedFields(getDefaultValues(), form.getValues());

  /**
   * 저장 클릭 진입점. 검증(필수 항목)이 항상 먼저고, 통과한 뒤에만 변경 여부를 본다.
   *
   * 변경이 없으면 검증조차 돌리지 않는다 — 보낼 것이 없는데 폼 전체 스키마를 돌리면
   * 서버가 빈 값으로 내려준 필드(계정의 소속 id 등) 때문에 손도 대지 않은 폼에
   * "필수 항목" 오류가 뜬다. 필수값을 비운 수정은 그 자체가 변경이라 이 분기를
   * 타지 않고 검증 오류가 그대로 우선한다.
   */
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (useChangedFieldsOnly && Object.keys(getChangedData()).length === 0) {
      event.preventDefault();
      toast.info("변경된 내용이 없습니다.");
      return;
    }
    return handleSubmit(onFormSubmit, onFormError)(event);
  };

  const onFormSubmit = () => {
    dialogs.open("save", handleConfirmSave);
  };

  const handleConfirmSave = async () => {
    if (isMutating) return;
    const currentData = form.getValues();

    let submitData: Partial<TFormValues>;
    if (useChangedFieldsOnly) {
      const changedData = getChangedData();
      // 다이얼로그를 여는 시점에 이미 걸러지지만, 확인까지 사이에 값이 되돌려진 경우를 막는다
      if (Object.keys(changedData).length === 0) {
        toast.info("변경된 내용이 없습니다.");
        return;
      }
      submitData = changedData;
    } else {
      submitData = currentData;
    }

    setIsMutating(true);
    updateMutate(
      { uuid, params: submitData },
      {
        onSuccess: (res) => {
          setIsMutating(false);
          onUpdateSuccess(res);
          setIsEditMode(false);
          reset(currentData);
        },
        onError: (error) => {
          setIsMutating(false);
          onUpdateError(error);
        },
      },
    );
  };

  const handleCloseAttempt = () => {
    if (isEditMode && isDirty) {
      dialogs.open("exit", () => setOpen(false));
    } else {
      setOpen(false);
    }
  };

  return (
    <>
      <Drawer
        direction="right"
        open={open}
        onOpenChange={setOpen}
        modal={false}
      >
        {!hideTrigger && (
          <DrawerTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              // 열린 행을 목록에서 하이라이트하는 마커 (table.tsx TableRow)
              data-row-drawer-open={open || undefined}
              className={cn(
                "cursor-pointer",
                triggerContent != null &&
                  "flex w-full items-center justify-between gap-2 text-left",
              )}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault(); // vaul 기본 토글 차단 후 직접 제어
                // 셀 텍스트를 드래그 선택한 경우 열기/닫기 동작 막기
                const selection = window.getSelection();
                if (
                  selection &&
                  selection.toString() &&
                  triggerRef.current?.contains(selection.anchorNode)
                ) {
                  return;
                }
                if (open) {
                  handleCloseAttempt();
                } else {
                  e.currentTarget.blur();
                  setOpen(true);
                }
              }}
            >
              {triggerContent != null && (
                <span className="truncate min-w-0 typo-body-2-medium select-text">
                  {triggerContent}
                </span>
              )}
              <span className="flex shrink-0">
                <SideClose width={24} height={24} color="labels-assistive" />
              </span>
            </button>
          </DrawerTrigger>
        )}

        <DrawerContent
          showOverlay={false}
          className={cn(
            "w-150 max-w-150 h-full border-none",
            "bg-bg-primary",
            "shadow-[-20px_0px_80px_0px_rgba(0,0,0,0.15)]",
          )}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            handleCloseAttempt();
          }}
          onPointerDownOutside={(e) => {
            // 드로어를 연 트리거(셀)를 다시 누른 경우 닫지 않고 유지
            const target = e.detail?.originalEvent?.target as Node | null;
            if (target && triggerRef.current?.contains(target)) return;
            // 행 클릭으로 여는 화면은 행의 onClick 이 토글한다 — 여기서 함께 닫으면
            // 다른 행을 눌러도 드로어가 전환되지 않고 닫히기만 한다
            if (
              hideTrigger &&
              target instanceof Element &&
              target.closest(`[${ROW_CLICKABLE_ATTR}]`)
            ) {
              return;
            }
            handleCloseAttempt();
          }}
        >
          <DrawerTitle className="hidden" />
          <DrawerDescription className="hidden" />
          <FormProvider {...form}>
            <form onSubmit={handleFormSubmit} className="flex flex-col h-full">
              {/* Header */}
              {isEditMode ? (
                <EditModeHeader
                  title={editTitle}
                  onClose={handleCloseAttempt}
                  onCancel={handleCancelEdit}
                  isSubmitting={isSubmitting || isMutating}
                />
              ) : (
                <ViewModeHeader
                  title={viewTitle}
                  subtitle={viewSubtitle}
                  titleExtra={viewTitleExtra}
                  belowTitle={viewBelowTitle}
                  onClose={handleCloseAttempt}
                  onEdit={handleEditMode}
                  onDelete={handleDeleteClick}
                  createdAt={createdAt}
                  updatedAt={updatedAt}
                  canWrite={canWrite}
                  hideActions={hideActions}
                  writeDisabledReason={writeDisabledReason}
                  canDelete={canDelete}
                  deleteDisabledReason={deleteDisabledReason}
                />
              )}

              {/* Contents - Scrollable */}
              <ScrollFadeArea
                wrapperClassName="flex-1 overflow-hidden"
                scrollRef={scrollRef}
                className="h-full px-10 py-6"
              >
                <div className="flex flex-col gap-6 mb-20">
                  {children({ isEditMode, isLoading })}
                </div>
              </ScrollFadeArea>

              {!isEditMode && viewFooter && (
                <div className="shrink-0 border-t border-lines-solid-normal px-10 py-6">
                  {viewFooter}
                </div>
              )}
            </form>
          </FormProvider>
        </DrawerContent>
      </Drawer>

      <dialogs.ConfirmDialogs />
    </>
  );
}

FormDrawer.displayName = "FormDrawer";
